import {
  GetListingsResponse,
  GetListingsItem,
  CreateListingConfig,
  ListingConfig,
} from "../../types/coveo-types";
import { debug, NativeDataFetcher } from "@sitecore-jss/sitecore-jss";

/**
 * Fetches all listings from the Coveo platform.
 *
 * @param {Object} options - The options for fetching listings.
 * @param {number} [options.perPage=10] - The number of listings to fetch per page.
 * @returns {Promise<GetListingsItem[]>} A promise that resolves to an array of listings.
 */
export const getListings = async ({
  perPage = 10,
}: {
  perPage?: number;
} = {}): Promise<GetListingsItem[]> => {
  const fetcher = new NativeDataFetcher({
    headers: [["Authorization", `Bearer ${process.env.CoveoListingApiKey}`]],
  });

  return await fetcher
    .get<GetListingsResponse>(
      `https://platform.cloud.coveo.com/rest/organizations/${process.env.NEXT_PUBLIC_CoveoOrganizationId}/commerce/v2/configurations/listings/names?trackingId=${process.env.NEXT_PUBLIC_CoveoTrackingId}&page=0&perPage=${perPage}`
    )
    .then(async (response) => {
      let listings: GetListingsItem[] = [];

      if (response.data?.items && response.data.items.length > 0) {
        listings = listings.concat(response.data.items);

        if (response.data.totalPages > 1) {
          let pagePromises: Promise<GetListingsItem[]>[] = [];
          for (let page = 1; page < response.data.totalPages; page++) {
            pagePromises.push(
              fetcher
                .get<GetListingsResponse>(
                  `https://platform.cloud.coveo.com/rest/organizations/${process.env.NEXT_PUBLIC_CoveoOrganizationId}/commerce/v2/configurations/listings/names?trackingId=${process.env.NEXT_PUBLIC_CoveoTrackingId}&page=${page}&perPage=${perPage}`
                )
                .then((response) => {
                  if (response.data?.items && response.data.items.length > 0) {
                    return response.data.items;
                  }

                  return [];
                })
                .catch((e) => {
                  debug.http(e);
                  return [];
                })
            );
          }

          const pagesData = await Promise.all(pagePromises);

          pagesData.forEach((data) => {
            listings = listings.concat(data);
          });
        }
      }

      return listings;
    })
    .catch((e) => {
      debug.http(e);
      return [];
    });
};

/**
 * Creates a new listing on the Coveo platform.
 *
 * @param {CreateListingConfig} data - The configuration data for the new listing.
 * @returns {Promise<ListingConfig | CreateListingConfig>} A promise that resolves to the created listing configuration or the input data if the request fails.
 */
export const createListing = async (
  data: CreateListingConfig
): Promise<ListingConfig | CreateListingConfig> => {
  const fetcher = new NativeDataFetcher({
    headers: [
      ["Content-Type", "application/json"],
      ["Authorization", `Bearer ${process.env.CoveoListingApiKey}`],
    ],
  });

  return await fetcher
    .post<ListingConfig>(
      `https://platform.cloud.coveo.com/rest/organizations/${process.env.NEXT_PUBLIC_CoveoOrganizationId}/commerce/v2/configurations/listings`,
      data
    )
    .then((response) => {
      return response.data;
    })
    .catch((e) => {
      console.log(e);
      return data;
    });
};
